import React, { useEffect, useState } from 'react';
import { Pressable, PressableProps, ScrollView } from 'react-native';
import { DNABox, DNABoxProps, DNAButton, DNAText } from '@alucio/lux-ui';
import { useCurrentUser } from 'src/state/redux/selector/user';
import { useDocumentSearchV2Context } from 'src/hooks/useDocumentSearchV2';
import { ALL_AI_SEARCH_FLOWS } from 'src/types/aiSearchFlows';
import {
  AI_SEARCH_HISTORY_UPDATED, AISearchHistoryEntry, listAISearchHistory, deleteAISearchHistory,
} from 'src/utils/aiSearchHistory';
import {
  ReferencesSection, SummarySection, filterTenantReferences,
} from 'src/components/Header/SearchInput/aiSearch/AISearchResults';
import { getSummaryParagraphs } from 'src/components/Header/SearchInput/aiSearch/useAISearchHooks';

import { exportAISearchHistoryCsv } from 'src/utils/aiSearchHistoryCsvExport';

const PressableDNABox = DNABox as React.ComponentType<DNABoxProps & PressableProps>;
const flowLabel = (id: string) => ALL_AI_SEARCH_FLOWS.find(flow => flow.id === id)?.label ?? id;
const duration = (ms: number | null) => ms === null ? '—' : `${(ms / 1000).toFixed(1)} s`;

const AISearchHistory: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const user = useCurrentUser();
  const userId = user.userProfile?.id;
  const tenantId = user.relations?.tenant?.id;
  const { allDocVersions } = useDocumentSearchV2Context();
  const [entries, setEntries] = useState<AISearchHistoryEntry[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [finding, setFinding] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setEntries([]);
    setSelectedId(null);
    setError(null);
    const refresh = async () => {
      if (!tenantId || !userId) return;
      try {
        const saved = await listAISearchHistory(tenantId, userId);
        if (active) { setEntries(saved); setError(null); }
      } catch {
        if (active) setError('Local history is unavailable. This browser must allow IndexedDB storage.');
      }
    };
    refresh();
    window.addEventListener(AI_SEARCH_HISTORY_UPDATED, refresh);
    return () => {
      active = false;
      window.removeEventListener(AI_SEARCH_HISTORY_UPDATED, refresh);
    };
  }, [tenantId, userId]);

  const ownedEntries = entries.filter(entry => entry.tenantId === tenantId && entry.userId === userId);
  const selected = ownedEntries.find(entry => entry.sessionId === selectedId);
  const paragraphs = selected?.report ? getSummaryParagraphs(selected.report) : [];
  const references = filterTenantReferences(selected?.references ?? [], allDocVersions);

  const deleteEntry = async (entry: AISearchHistoryEntry) => {
    if (!tenantId || !userId || deleting) return;
    setDeleting(true);
    setActionError(null);
    try {
      await deleteAISearchHistory(tenantId, userId, entry.sessionId);
      setEntries(current => current.filter(saved => saved.sessionId !== entry.sessionId));
      if (selectedId === entry.sessionId) {
        setSelectedId(null);
        setFinding(null);
      }
    } catch {
      setActionError('Could not delete this search. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const exportEntries = async (rows: AISearchHistoryEntry[]) => {
    setActionError(null);
    try { await exportAISearchHistoryCsv(rows); }
    catch { setActionError('Could not export searches. Please try again.'); }
  };

  return (
    <ScrollView style={{ maxHeight: '100%' }} contentContainerStyle={{ padding: 20 }}>
      <DNABox appearance="col" spacing="md">
        <DNAText h3>AI Search History</DNAText>
        <DNAText>
          Searches are saved only in this browser, for your current user and tenant. Select a row to open
          the saved answer and returned documents without running the search again.
        </DNAText>
        <DNAButton
          appearance="ghost"
          size="sm"
          disabled={ownedEntries.length === 0}
          onPress={() => exportEntries(ownedEntries)}
        >
          Export all as CSV
        </DNAButton>
        {actionError && <DNAText>{actionError}</DNAText>}
        {error && <DNAText>{error}</DNAText>}
        {!error && ownedEntries.length === 0 && (
          <DNAText>No saved searches yet. New AI searches will appear here.</DNAText>
        )}
        {compact && selected && (
          <DNAButton
            appearance="ghost"
            size="sm"
            iconLeft="arrow-left"
            onPress={() => { setSelectedId(null); setFinding(null); }}
          >
            All searches
          </DNAButton>
        )}
        {(!compact || !selected) && <ScrollView horizontal={!compact}>
          <DNABox appearance="col" style={compact ? { width: '100%' } : { minWidth: 1100 }}>
            {!compact && <DNABox appearance="row" style={{ padding: 10 }}>
              {['Started', 'Question', 'Flow', 'Duration', 'Documents', 'Status'].map((title, index) => (
                <DNAText key={title} bold style={{ width: [180, 300, 220, 90, 90, 120][index] }}>{title}</DNAText>
              ))}
            </DNABox>}
            {ownedEntries.map(entry => (
              <DNABox key={entry.sessionId} appearance="row" alignY="center">
                <PressableDNABox
                  as={Pressable}
                  accessibilityRole="button"
                  accessibilityLabel={`Open saved search: ${entry.searchText}`}
                  accessibilityState={{ selected: selectedId === entry.sessionId }}
                  appearance={compact ? 'col' : 'row'}
                  style={{
                    flex: 1,
                    padding: 10,
                    borderTopWidth: 1,
                    borderColor: '#ddd',
                    backgroundColor: selectedId === entry.sessionId ? '#eef4ff' : undefined,
                  }}
                  onPress={() => { setSelectedId(entry.sessionId); setFinding(null); }}
                >
                  <DNAText style={compact ? undefined : { width: 180 }}>
                    {new Date(entry.startedAt).toLocaleString()}
                  </DNAText>
                  <DNAText style={compact ? undefined : { width: 300 }}>
                    {entry.searchText}
                  </DNAText>
                  <DNAText style={compact ? undefined : { width: 220 }}>
                    {flowLabel(entry.flowId)}
                  </DNAText>
                  <DNAText style={compact ? undefined : { width: 90 }}>
                    {duration(entry.durationMs)}
                  </DNAText>
                  <DNAText style={compact ? undefined : { width: 90 }}>
                    {`${new Set(entry.references.map(ref => ref.id)).size}${compact ? ' documents' : ''}`}
                  </DNAText>
                  <DNAText style={compact ? undefined : { width: 120 }}>
                    {entry.status === 'running' ? 'Not completed' : entry.status}
                  </DNAText>
                </PressableDNABox>
                <DNAButton
                  appearance="ghost"
                  size="sm"
                  disabled={deleting}
                  accessibilityLabel={`Delete saved search: ${entry.searchText}`}
                  onPress={() => deleteEntry(entry)}
                >
                  Delete
                </DNAButton>
              </DNABox>
            ))}
          </DNABox>
        </ScrollView>}
        {selected && (
          <DNABox appearance="col" spacing="md">
            <DNABox appearance="row" spacing="sm">
              <DNAButton appearance="ghost" size="sm" onPress={() => exportEntries([selected])}>
                Export search as CSV
              </DNAButton>
              <DNAButton appearance="ghost" size="sm" disabled={deleting} onPress={() => deleteEntry(selected)}>
                Delete search
              </DNAButton>
            </DNABox>
            <DNAText h4>{selected.searchText}</DNAText>
            <DNAText>
              {`${flowLabel(selected.flowId)} · ${duration(selected.durationMs)}`}
              {` · ${new Date(selected.startedAt).toLocaleString()}`}
            </DNAText>
            <DNAText>{`Session: ${selected.sessionId} · Index: ${selected.indexName}`}</DNAText>
            {selected.status === 'running' ? <DNAText>No completed response was saved for this search.</DNAText> : (
              <SummarySection
                loading={false}
                error={selected.error}
                report={selected.report}
                statusMessage=""
                hasSubmittedSearch
                searchText={selected.searchText}
                summaryParagraphs={paragraphs}
                selectedFindingId={finding}
                onFindingSelect={setFinding}
              />
            )}
            <ReferencesSection
              loading={false}
              report={selected.report}
              references={references}
              selectedFinding={finding === null ? null : paragraphs[finding]}
              onClearFilter={() => setFinding(null)}
            />
            {selected.references.filter(ref => !references.includes(ref)).map((ref, index) => (
              <DNAText key={`${ref.id}-${index}`}>
                {`${ref.title} · Page ${ref.page_number ?? '—'} · Unavailable in the current library`}
              </DNAText>
            ))}
          </DNABox>
        )}
      </DNABox>
    </ScrollView>
  );
};

export default AISearchHistory;
